import { RootState } from '@/hooks/useStore';
import {
  Store,
  ActionContext,
  ActionTree,
  GetterTree,
  MutationTree,
  CommitOptions,
  DispatchOptions,
  Module,
} from 'vuex';
import type { ExtParams } from 'src/models';
import type { JohaisetsuReportLocalStorage } from '@/models/johaisetsu/johaisetsuCommon';
import type { NotificationLocalStorage } from '@/models/notification';
import type { CleaningReportLocalStorage } from '@/models/spCleaningReport';
import type { Tool1LocalStorage } from '@/models/top';
import type { StandardReportLocalStorage } from '@/models/externalReports/standard/spStandardReport';
import type {
  HokkaidoAirportReportLocalStorage,
  HokkaidoAirportVehicleSelection,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type { EmergencyResponseTeamReportLocalStorage } from '@/models/externalReports/emergencyResponseTeam/spEmergencyResponseTeamReport';

const localStorageName = 'rt_streaming_ap';

export type CarFilterGroupScope = 'g2' | 'g3' | null;

export interface Preference {
  car_filter_group_scope: CarFilterGroupScope;
}

export interface LocalStorageState {
  serverToken: string;
  preferences: Preference;
  extParams: ExtParams;
  standardReportSpStorage: StandardReportLocalStorage | null;
  hokkaidoAirportReportSpStorage: HokkaidoAirportReportLocalStorage | null;
  emergencyResponseTeamReportSpStorage: EmergencyResponseTeamReportLocalStorage | null;
  johaisetsuReportSpStorage: JohaisetsuReportLocalStorage | null;
  settouPatrolSpStorage: JohaisetsuReportLocalStorage | null;
  cleaningReportSpStorage: CleaningReportLocalStorage | null;
  notificationStorage: NotificationLocalStorage | null;
  tool1Storage: Tool1LocalStorage | null;
  johaisetsuDeviceIds: Record<string, string>; // userId(文字列化) → deviceId
  hokkaidoAirportLastStandbyType: string | null;
  hokkaidoAirportLastVehicleSelection: HokkaidoAirportVehicleSelection | null;
}

// the definition of initial state is required.
const state: LocalStorageState = {
  serverToken: '',
  preferences: {
    car_filter_group_scope: null,
  },
  extParams: {},
  standardReportSpStorage: null,
  hokkaidoAirportReportSpStorage: null,
  emergencyResponseTeamReportSpStorage: null,
  johaisetsuReportSpStorage: null,
  settouPatrolSpStorage: null,
  cleaningReportSpStorage: null,
  notificationStorage: null,
  tool1Storage: null,
  johaisetsuDeviceIds: {},
  hokkaidoAirportLastStandbyType: null,
  hokkaidoAirportLastVehicleSelection: null,
};

const cpProps: Array<keyof LocalStorageState> = [
  'serverToken',
  'preferences',
  'extParams',
  'standardReportSpStorage',
  'hokkaidoAirportReportSpStorage',
  'emergencyResponseTeamReportSpStorage',
  'johaisetsuReportSpStorage',
  'settouPatrolSpStorage',
  'cleaningReportSpStorage',
  'notificationStorage',
  'tool1Storage',
  'johaisetsuDeviceIds',
  'hokkaidoAirportLastStandbyType',
  'hokkaidoAirportLastVehicleSelection',
];

try {
  const storedItem = localStorage.getItem(localStorageName);
  if (storedItem) {
    const config = JSON.parse(storedItem);
    cpProps.forEach(key => {
      if (config[key]) {
        state[key] = config[key];
      }
    });
  }
} catch (e) {}

export enum LocalStorageGetterTypes {
  SERVER_TOKEN = 'serverToken',
  PREFERENCES = 'preferences',
  EXT_PARAMS = 'extParams',
  STANDARD_REPORT_SP_STORAGE = 'standardReportSpStorage',
  HOKKAIDO_AIRPORT_REPORT_SP_STORAGE = 'hokkaidoAirportReportSpStorage',
  EMERGENCY_RESPONSE_TEAM_REPORT_SP_STORAGE = 'emergencyResponseTeamReportSpStorage',
  JOHAISETSU_REPORT_SP_STORAGE = 'johaisetsuReportSpStorage',
  SETTOU_PATROL_SP_STORAGE = 'settouPatrolSpStorage',
  CLEANING_REPORT_SP_STORAGE = 'cleaningReportSpStorage',
  NOTIFICATION_STORAGE = 'notificationStorage',
  TOOL1_STORAGE = 'tool1Storage',
  JOHAISETSU_DEVICE_IDS = 'johaisetsuDeviceIds',
  HOKKAIDO_AIRPORT_LAST_STANDBY_TYPE = 'hokkaidoAirportLastStandbyType',
  HOKKAIDO_AIRPORT_LAST_VEHICLE_SELECTION = 'hokkaidoAirportLastVehicleSelection',
}

export interface LocalStorageGetters<S = LocalStorageState> {
  [LocalStorageGetterTypes.SERVER_TOKEN](state: S): string;
  [LocalStorageGetterTypes.PREFERENCES](state: S): Preference;
  [LocalStorageGetterTypes.EXT_PARAMS](state: S): ExtParams;
  [LocalStorageGetterTypes.STANDARD_REPORT_SP_STORAGE](state: S): StandardReportLocalStorage | null;
  [LocalStorageGetterTypes.HOKKAIDO_AIRPORT_REPORT_SP_STORAGE](state: S): HokkaidoAirportReportLocalStorage | null;
  [LocalStorageGetterTypes.EMERGENCY_RESPONSE_TEAM_REPORT_SP_STORAGE](state: S): EmergencyResponseTeamReportLocalStorage | null;
  [LocalStorageGetterTypes.JOHAISETSU_REPORT_SP_STORAGE](state: S): JohaisetsuReportLocalStorage | null;
  [LocalStorageGetterTypes.SETTOU_PATROL_SP_STORAGE](state: S): JohaisetsuReportLocalStorage | null;
  [LocalStorageGetterTypes.CLEANING_REPORT_SP_STORAGE](state: S): CleaningReportLocalStorage | null;
  [LocalStorageGetterTypes.NOTIFICATION_STORAGE](state: S): NotificationLocalStorage | null;
  [LocalStorageGetterTypes.TOOL1_STORAGE](state: S): Tool1LocalStorage | null;
  [LocalStorageGetterTypes.JOHAISETSU_DEVICE_IDS](state: S): Record<string, string>;
  [LocalStorageGetterTypes.HOKKAIDO_AIRPORT_LAST_STANDBY_TYPE](state: S): string | null;
  [LocalStorageGetterTypes.HOKKAIDO_AIRPORT_LAST_VEHICLE_SELECTION](state: S): HokkaidoAirportVehicleSelection | null;
}

const getters: GetterTree<LocalStorageState, RootState> & LocalStorageGetters = {
  [LocalStorageGetterTypes.SERVER_TOKEN]: (state) => state.serverToken,
  [LocalStorageGetterTypes.PREFERENCES]: (state) => state.preferences,
  [LocalStorageGetterTypes.EXT_PARAMS]: (state) => state.extParams,
  [LocalStorageGetterTypes.STANDARD_REPORT_SP_STORAGE]: (state) => state.standardReportSpStorage,
  [LocalStorageGetterTypes.HOKKAIDO_AIRPORT_REPORT_SP_STORAGE]: (state) => state.hokkaidoAirportReportSpStorage,
  [LocalStorageGetterTypes.EMERGENCY_RESPONSE_TEAM_REPORT_SP_STORAGE]: (state) => state.emergencyResponseTeamReportSpStorage,
  [LocalStorageGetterTypes.JOHAISETSU_REPORT_SP_STORAGE]: (state) => state.johaisetsuReportSpStorage,
  [LocalStorageGetterTypes.SETTOU_PATROL_SP_STORAGE]: (state) => state.settouPatrolSpStorage,
  [LocalStorageGetterTypes.CLEANING_REPORT_SP_STORAGE]: (state) => state.cleaningReportSpStorage,
  [LocalStorageGetterTypes.NOTIFICATION_STORAGE]: (state) => state.notificationStorage,
  [LocalStorageGetterTypes.TOOL1_STORAGE]: (state) => state.tool1Storage,
  [LocalStorageGetterTypes.JOHAISETSU_DEVICE_IDS]: (state) => state.johaisetsuDeviceIds,
  [LocalStorageGetterTypes.HOKKAIDO_AIRPORT_LAST_STANDBY_TYPE]: (state) => state.hokkaidoAirportLastStandbyType,
  [LocalStorageGetterTypes.HOKKAIDO_AIRPORT_LAST_VEHICLE_SELECTION]: (state) => {
    return state.hokkaidoAirportLastVehicleSelection;
  },
};
export enum LocalStorageMutationTypes {
  SET_LOCALSTORAGE = 'SET_LOCALSTORAGE',
}

type LocalStorageKeys = keyof LocalStorageState;
type LocalStorageValTypes = LocalStorageState[LocalStorageKeys];

type KeyVal = {
  key: LocalStorageKeys;
  val: LocalStorageValTypes;
};

interface MutationPayload {
  data: Partial<LocalStorageState>;
}

export type LocalStorageMutations<S = LocalStorageState> = {
  [LocalStorageMutationTypes.SET_LOCALSTORAGE](state: S, payload: MutationPayload): void;
};

const mutations: MutationTree<LocalStorageState> & LocalStorageMutations = {
  [LocalStorageMutationTypes.SET_LOCALSTORAGE](state, payload): void {
    state.serverToken = payload.data.serverToken ?? state.serverToken;
    state.preferences = payload.data.preferences ?? state.preferences;
    state.extParams = payload.data.extParams ?? state.extParams;
    state.standardReportSpStorage = getLocalStorageValue('standardReportSpStorage', payload);
    state.hokkaidoAirportReportSpStorage = getLocalStorageValue('hokkaidoAirportReportSpStorage', payload);
    state.emergencyResponseTeamReportSpStorage = getLocalStorageValue('emergencyResponseTeamReportSpStorage', payload);
    state.johaisetsuReportSpStorage = getLocalStorageValue('johaisetsuReportSpStorage', payload);
    state.settouPatrolSpStorage = getLocalStorageValue('settouPatrolSpStorage', payload);
    state.cleaningReportSpStorage = getLocalStorageValue('cleaningReportSpStorage', payload);
    state.notificationStorage = getLocalStorageValue('notificationStorage', payload);
    state.tool1Storage = getLocalStorageValue('tool1Storage', payload);
    state.johaisetsuDeviceIds = getLocalStorageValue('johaisetsuDeviceIds', payload);
    state.hokkaidoAirportLastStandbyType = getLocalStorageValue('hokkaidoAirportLastStandbyType', payload);
    state.hokkaidoAirportLastVehicleSelection = getLocalStorageValue('hokkaidoAirportLastVehicleSelection', payload);
    localStorage.setItem(localStorageName, JSON.stringify(state));
  },
};

const getLocalStorageValue = <K extends LocalStorageKeys>(key: K, payload: MutationPayload): LocalStorageState[K] => {
  const data = payload.data;
  if (!data || !Object.prototype.hasOwnProperty.call(data, key)) {
    return state[key];
  }

  return data[key] as LocalStorageState[K];
};

export enum LocalStorageActionTypes {
  GET = 'get',
  SET = 'set',
  SET_PREFERENCE = 'setPreference',
}

type AugmentedActionContext = {
  commit<K extends keyof LocalStorageMutations>(
    key: K,
    payload?: Parameters<LocalStorageMutations[K]>[1]
  ): ReturnType<LocalStorageMutations[K]>;
  getters: {
    [K in keyof LocalStorageGetters]: ReturnType<LocalStorageGetters[K]>
  };
} & Omit<ActionContext<LocalStorageState, RootState>, 'commit' | 'getters'>;

export interface LocalStorageActions {
  [LocalStorageActionTypes.GET]({ commit, getters }: AugmentedActionContext, param: { key: LocalStorageKeys }): Promise<LocalStorageValTypes>;
  [LocalStorageActionTypes.SET]({ commit }: AugmentedActionContext, data: KeyVal): void;
  [LocalStorageActionTypes.SET_PREFERENCE]({ commit }: AugmentedActionContext, data: { key: 'car_filter_group_scope'; val: 'g2' | 'g3' | null }): void;
}

const actions: ActionTree<LocalStorageState, RootState> & LocalStorageActions = {
  [LocalStorageActionTypes.GET](_, { key }): Promise<LocalStorageValTypes> {
    return Promise.resolve(state[key]);
  },
  [LocalStorageActionTypes.SET]({ commit }, { key, val }): void {
    const data = { [key]: val };
    commit(LocalStorageMutationTypes.SET_LOCALSTORAGE, { data });
  },
  [LocalStorageActionTypes.SET_PREFERENCE]({ commit }, { key, val }): void {
    const pref = Object.assign({}, state.preferences);
    pref[key] = val;
    const data = { preferences: pref };
    commit(LocalStorageMutationTypes.SET_LOCALSTORAGE, { data });
  },
};

export type LocalStorageStore<S = LocalStorageState> = Omit<Store<S>, 'getters' | 'commit' | 'dispatch'>
& {
  getters: {
    [K in keyof LocalStorageGetters]: ReturnType<LocalStorageGetters[K]>
  };
} & {
  commit<K extends keyof LocalStorageMutations, P extends Parameters<LocalStorageMutations[K]>[1]>(
    key: K,
    payload?: P,
    options?: CommitOptions
  ): ReturnType<LocalStorageMutations[K]>;
} & {
  dispatch<K extends keyof LocalStorageActions>(
    key: K,
    payload?: Parameters<LocalStorageActions[K]>[1],
    options?: DispatchOptions
  ): ReturnType<LocalStorageActions[K]>;
};

export const store: Module<LocalStorageState, RootState> = {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
